import React, { useState } from 'react';
import { Collapse, Input, Button } from 'reactstrap';
import { formatStatusValue } from '../../utils/formatStatus';

const formatValue = (key, value) => {
    const statusLabel = formatStatusValue(key, value);
    if (statusLabel) return statusLabel;
    if (value === null || value === undefined) return 'N/A';
    if (Array.isArray(value)) return value.join(', ') || 'N/A';
    if (typeof value === 'object') return JSON.stringify(value);
    return value.toString() || 'N/A';
};

// Raw string representation used to seed an editable input
const toEditString = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

// Coerce an edited string back to the original value's type so we don't
// silently turn booleans/numbers into strings.
const coerceValue = (original, next) => {
    if (typeof original === 'boolean') return next === 'true';
    if (typeof original === 'number') {
        const parsed = Number(next);
        return next === '' || Number.isNaN(parsed) ? next : parsed;
    }
    if (Array.isArray(original)) {
        return next.split(',').map(item => item.trim()).filter(Boolean);
    }
    return next;
};

const JsonDisplay = ({ data, title = 'Details', defaultOpen = false, collapsible = true, editable = false, onChange }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [isEditing, setIsEditing] = useState(false);

    if (!data || Object.keys(data).length === 0) return null;

    const entries = Object.entries(data);

    const handleFieldChange = (key, rawValue) => {
        onChange?.({ ...data, [key]: coerceValue(data[key], rawValue) });
    };

    const renderField = (key, value) => {
        if (!isEditing) {
            return (
                <div className="text-white/50" style={{ overflowWrap: 'anywhere' }}>
                    {formatValue(key, value)}
                </div>
            );
        }

        if (typeof value === 'boolean') {
            return (
                <Input
                    type="select"
                    bsSize="sm"
                    value={String(value)}
                    onChange={e => handleFieldChange(key, e.target.value)}
                >
                    <option value="true">true</option>
                    <option value="false">false</option>
                </Input>
            );
        }

        return (
            <Input
                type={typeof value === 'number' ? 'number' : 'text'}
                bsSize="sm"
                value={toEditString(value)}
                onChange={e => handleFieldChange(key, e.target.value)}
            />
        );
    };

    const renderContent = () => (
        <>
            {editable && (
                <div className="d-flex justify-content-end mb-2">
                    <Button type="button" size="sm" color="primary" outline onClick={() => setIsEditing(!isEditing)}>
                        <span className={`fas fa-${isEditing ? 'check' : 'pen'} me-2`} />
                        {isEditing ? 'Done' : 'Edit'}
                    </Button>
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {entries.map(([key, value], index) => (
                    <div key={index} style={{ minWidth: 0 }}>
                        <div className="text-uppercase fw-bold small">
                            {key.replace(/([a-z])([A-Z])/g, '$1 $2')}
                        </div>
                        {renderField(key, value)}
                    </div>
                ))}
            </div>
        </>
    );

    if (!collapsible) {
        return (
            <div className="json-display">
                {renderContent()}
            </div>
        );
    }

    return (
        <div className="json-display">
            <div
                className={`d-flex align-items-center p-2 bg-primary bg-opacity-25 cursor-pointer rounded${isOpen ? '-top' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className={`fas fa-chevron-${isOpen ? 'up' : 'down'} me-2`} />
                <span className="flex-grow-1">
                    {title}
                    <span className="small ms-2 text-white-50">
                        ({entries.length} properties)
                    </span>
                </span>
            </div>
            <Collapse isOpen={isOpen}>
                <div className="p-3 bg-primary bg-opacity-10 rounded-bottom">
                    {renderContent()}
                </div>
            </Collapse>
        </div>
    );
};

export default JsonDisplay;
